import { drugImageMaxHeight, drugImageMaxWidth } from '../constants';
import type {
    MedicationConfigOverride,
    MedicationOverride,
} from '../hooks/use-config-api';
import type { DrugConfig } from '../types/drug-config';

/**
 * Gets the image URL for a specific NDC.
 * Checks config override first, then falls back to parent DrugConfig image.
 *
 * @param drugConfig - The parent drug configuration
 * @param ndc - The NDC code to look up
 * @param configOverride - The medication config override (contains NDC-specific images and pricing)
 * @returns The image URL to use for this NDC
 *
 * @example
 * const imageUrl = getNdcImage(drugConfig, '00002356611', configOverride);
 * // Returns: '/static/img/zepbound-kwikpen.png' (from override)
 * // or falls back to drugConfig.imageUrl if not in override
 */
export const getNdcImage = (
    drugConfig: DrugConfig,
    ndc: string,
    configOverride: MedicationConfigOverride | null
): string => {
    const overrideImage = configOverride?.ndcInfo[ndc]?.imageUrl;

    if (overrideImage) {
        return overrideImage;
    }

    return drugConfig.imageUrl;
};

export const getNdcImageOrientation = (
    ndc: string,
    configOverride: MedicationConfigOverride | null
): Required<MedicationOverride>['imageOrientation'] =>
    configOverride?.ndcInfo[ndc]?.imageOrientation ?? 'portrait';

export const getNdcImageMaxDimensions = (
    orientation: Required<MedicationOverride>['imageOrientation']
) => {
    const maxHeight =
        orientation === 'landscape' ? drugImageMaxHeight : drugImageMaxWidth;
    const maxWidth =
        orientation === 'landscape' ? drugImageMaxWidth : drugImageMaxHeight;

    return { maxHeight, maxWidth };
};
